/**
 * interim — the live words in the listening strip, and nothing else.
 *
 * The browser's own speech recognition, where it exists, is instant and free
 * and noticeably less accurate than the transcription tier — especially on
 * names, which is the error nobody spots. So it is used for exactly one thing:
 * showing the speaker that they are being heard while they talk.
 *
 * WHAT LANDS IN THE DRAFT IS NEVER THIS. The authoritative text always comes
 * back from the server, per pause. This text is display-only, lives for a
 * second or two, and is discarded the moment the real words arrive. Reversing
 * that — letting the browser's guess become the message — is what would make
 * dictation "fast but wrong", which is the trade Miles explicitly did not want.
 *
 * Absent in Firefox and some embedded browsers. Absence costs nothing: the
 * strip then shows the level meter and the elapsed time, which is already
 * enough to prove the microphone is live.
 */

type RecognitionCtor = new () => SpeechRecognitionLike;

interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: any) => void) | null;
  onerror: ((event: any) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}

function ctor(): RecognitionCtor | null {
  const w = window as unknown as {
    SpeechRecognition?: RecognitionCtor;
    webkitSpeechRecognition?: RecognitionCtor;
  };
  return w?.SpeechRecognition || w?.webkitSpeechRecognition || null;
}

export function interimAvailable(): boolean {
  return typeof window !== 'undefined' && ctor() !== null;
}

export interface InterimHandle {
  stop: () => void;
}

/**
 * Start reporting partial words. `onWords` is called with whatever the browser
 * currently thinks it heard; the caller renders it and never stores it.
 */
export function startInterim(
  language: 'en' | 'es',
  onWords: (words: string) => void,
): InterimHandle | null {
  const Ctor = ctor();
  if (!Ctor) return null;
  let recognition: SpeechRecognitionLike;
  try {
    recognition = new Ctor();
  } catch {
    return null;
  }

  let stopped = false;
  recognition.continuous = true;
  recognition.interimResults = true;
  recognition.lang = language === 'es' ? 'es-ES' : 'en-US';
  recognition.onresult = (event: any) => {
    try {
      const results = event?.results;
      if (!results) return;
      let words = '';
      for (let i = event.resultIndex ?? 0; i < results.length; i += 1) {
        words += String(results[i]?.[0]?.transcript || '');
      }
      const trimmed = words.trim();
      if (trimmed) onWords(trimmed);
    } catch {
      /* a malformed event is not worth interrupting a dictation for */
    }
  };
  recognition.onerror = () => { /* the strip simply shows no words */ };
  recognition.onend = () => {
    // Chrome ends the session on its own after a silence. While the microphone
    // is still open, restart it so the words keep appearing.
    if (stopped) return;
    try { recognition.start(); } catch { /* it will not come back; the meter remains */ }
  };

  try {
    recognition.start();
  } catch {
    return null;
  }

  return {
    stop: () => {
      stopped = true;
      recognition.onresult = null;
      recognition.onend = null;
      try { recognition.abort(); } catch { /* already gone */ }
    },
  };
}
